/* eslint-disable no-param-reassign */
const TIMER_DURATION_SECONDS = 30;
const TIMER_INTERVAL_MS = 1000;

/**
 * Handler class for managing resend timer and button states
 * Provides utilities for countdown timers and button state management in verification flows
 */
class ResendTimerHandler {
  /**
   * Updates timer display and button state during countdown
   * @param {number} seconds - Remaining seconds in countdown
   * @param {Object} elements - DOM elements to update
   * @param {HTMLElement} elements.resendBtn - Resend button element
   * @param {HTMLElement} elements.timerSpan - Timer text display element
   * @param {HTMLElement} elements.timerContainer - Timer container element
   * @param {HTMLElement} elements.contactSupport - Contact support section element
   * @param {HTMLElement} modal - Modal element containing the timer
   * @private
   */
  static updateTimerDisplay(seconds, elements, modal) {
    if (seconds <= 0) {
      clearInterval(modal.resendTimer);

      modal.resendTimer = null;

      elements.resendBtn.disabled = false;

      elements.resendBtn.style.display = "inline-block";

      elements.resendBtn.innerHTML = '<i class="fa fa-redo"></i> Resend';

      if (elements.timerContainer) {
        elements.timerContainer.style.display = "none";
      }
      if (elements.contactSupport) {
        elements.contactSupport.style.display = "block";
      }
    } else if (elements.timerSpan) {
      elements.timerSpan.textContent = seconds;
    }
  }

  /**
   * Starts a countdown timer for the resend button
   * Disables button and shows countdown, enables button when timer completes
   * @param {HTMLElement} modal - Modal element containing the timer
   * @returns {number|null} The interval ID or null if resend button not found
   */
  static startResendTimer(modal) {
    const elements = {
      resendBtn: modal.querySelector("#resend-code-btn"),
      timerSpan: modal.querySelector("#resend-timer"),
      timerContainer: modal.querySelector("#resend-timer-container"),
      contactSupport: modal.querySelector("#contact-support-section")
    };

    if (!elements.resendBtn) return null;

    let seconds = TIMER_DURATION_SECONDS;
    elements.resendBtn.disabled = true;
    elements.resendBtn.style.display = "none";

    if (elements.timerSpan) elements.timerSpan.textContent = seconds;
    if (elements.timerContainer) elements.timerContainer.style.display = "block";
    if (elements.contactSupport) elements.contactSupport.style.display = "none";

    const interval = setInterval(() => {
      seconds -= 1;
      ResendTimerHandler.updateTimerDisplay(seconds, elements, modal);
    }, TIMER_INTERVAL_MS);

    modal.resendTimer = interval;
    return interval;
  }

  /**
   * Sets the resend button to loading state
   * Shows spinner and disables button interaction
   * @param {HTMLElement} resendBtn - The resend button element
   * @param {HTMLElement} errorDiv - The error display element
   */
  static setResendButtonLoading(resendBtn, errorDiv) {
    if (resendBtn) {
      resendBtn.disabled = true;
      const originalContent = resendBtn.innerHTML;

      resendBtn.innerHTML = '<i class="fa fa-spinner fa-spin"></i> Sending...';

      resendBtn.dataset.originalContent = originalContent;
    }

    if (errorDiv) errorDiv.style.display = "none";
  }

  /**
   * Restores the resend button from loading state and shows error message
   * Re-enables button and displays error if operation failed
   * @param {Object} elements - DOM elements to update
   * @param {HTMLElement} elements.resendBtn - Resend button element
   * @param {HTMLElement} elements.errorDiv - Error container element
   * @param {HTMLElement} elements.errorText - Error text element
   * @param {string} message - Error message to display
   */
  static restoreResendButton(elements, message) {
    const { resendBtn, errorDiv, errorText } = elements;

    if (errorText) errorText.textContent = message || "Failed to send verification code";
    if (errorDiv) errorDiv.style.display = "block";

    if (resendBtn) {
      resendBtn.disabled = false;
      resendBtn.innerHTML =
        resendBtn.dataset.originalContent || '<i class="fa fa-redo"></i> Resend';
      delete resendBtn.dataset.originalContent;
    }
  }
}

export default ResendTimerHandler;
